import { useState, useEffect } from 'react';
import routes from '@root/routes';
import api from '@shared/api';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';

const defaultSearchResults = (locations = []) => ({ buildings: [], locations });
const defaultSearchValues = (initialLocations = []) =>
  initialLocations.map(location => ({
    key: location.slug,
    label: location.name,
    highlightedLabel: location.name,
    labelAsideText: `${location.locality}, ${location.abbreviatedRegion}`,
    value: location.slug,
  }));

const useUniversalSearch = ({
  history,
  queryString,
  marketsBySlug,
  startingMarketSlugs,
  marketSlug,
}) => {
  const criteria = ListingSearchCriteria.fromUrl({
    queryString,
    marketSlug,
  });

  const initialLocations = startingMarketSlugs.map(slug => marketsBySlug[slug]);
  const [searchResults, setSearchResults] = useState(
    defaultSearchResults(defaultSearchValues(initialLocations)),
  );

  const { buildings, locations } = searchResults;
  const [searchValues, setSearchValues] = useState(
    criteria.isUsingMap() ? [{ key: 'custom-map' }] : defaultSearchValues(initialLocations),
  );

  const [inputValue, setInputValue] = useState();
  const [dontRedirect, setDontRedirect] = useState(true);
  const clearMapCriteria = () => {
    criteria.remove('polygons');
    criteria.remove('map');
    criteria.remove('markets');
    history.push(criteria.toUrl());
  };

  const clearSearchResults = () => setSearchResults(defaultSearchResults());
  const resetSearch = () => {
    setSearchValues(defaultSearchValues());
    clearSearchResults();
    if (criteria.isUsingMap()) {
      clearMapCriteria();
    }
  };

  const handleSearch = async searchQuery => {
    const response = await api.fetch(routes.api.globalSearches(searchQuery));
    setSearchResults(await response.json());
  };

  useEffect(() => {
    let hasCustomMapKey = false;

    searchValues.forEach(item => {
      if (item.key === 'custom-map') {
        hasCustomMapKey = true;
      }
    });

    if (criteria.isUsingMap() && !hasCustomMapKey) {
      setSearchValues([...searchValues, { key: 'custom-map' }]);
    }
  }, [queryString]);

  const onSelect = optionValue => {
    const [type, value] = optionValue.key.split('|');
    if (type === 'building') {
      setDontRedirect(true);
      resetSearch();
      history.push(routes.building(value));
    } else {
      setSearchValues([...searchValues, optionValue]);
    }
  };

  const onDeselect = optionValue => {
    setSearchValues(searchValues.filter(val => val.key !== optionValue.key));

    if (optionValue.value === 'custom-map') {
      clearMapCriteria();
    }
  };

  const onSearch = searchInput => {
    handleSearch(searchInput);
    setInputValue(searchInput);
  };

  useEffect(() => {
    // when search values change because they are reset from going to a building, we don't
    // redirect to the search page
    if (dontRedirect || criteria.isUsingMap()) {
      setDontRedirect(false);
      return;
    }
    clearSearchResults();

    const asValues = searchValues.map(val => val.key);
    criteria.changeMarkets(asValues);
    history.push(criteria.toUrl());

    // eslint-disable-next-line
  }, [searchValues]);

  return {
    setSearchResults,
    handleSearch,
    resetSearch,
    searchValues,
    setSearchValues,
    onSelect,
    onDeselect,
    inputValue,
    onSearch,
    buildings,
    locations,
    isUsingMap: criteria.isUsingMap,
  };
};

export default useUniversalSearch;
